/**
 * MessageInput - Compose area with markdown, @mention, file drag-drop, and paperclip upload.
 */

import React, { useState, useCallback, useRef, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { useChat } from '../../contexts/ChatContext';
import { chatMessagesApi, chatAttachmentsApi } from '../../services/chatApi';

interface MessageInputProps {
  threadParentId?: string;
  onMessageSent?: () => void;
}

interface PendingFile {
  file: File;
  fileId?: string;
  progress: number;
  status: 'uploading' | 'uploaded' | 'error';
}

function formatFileSize(bytes: number): string {
  if (bytes < 1024) return bytes + ' B';
  if (bytes < 1024 * 1024) return (bytes / 1024).toFixed(1) + ' KB';
  return (bytes / (1024 * 1024)).toFixed(1) + ' MB';
}

const MessageInput: React.FC<MessageInputProps> = ({ threadParentId, onMessageSent }) => {
  const { t } = useTranslation('chat');
  const { currentChannel, sendTypingStart, sendTypingStop, addMessage } = useChat();
  const [content, setContent] = useState('');
  const [pendingFiles, setPendingFiles] = useState<PendingFile[]>([]);
  const [sending, setSending] = useState(false);
  const textareaRef = useRef<HTMLTextAreaElement>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const typingTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  const uploadedFiles = pendingFiles.filter(f => f.status === 'uploaded');
  const hasUploading = pendingFiles.some(f => f.status === 'uploading');
  const canSend = (content.trim() || uploadedFiles.length > 0) && !hasUploading && !sending;

  const handleChange = useCallback((e: React.ChangeEvent<HTMLTextAreaElement>) => {
    setContent(e.target.value);

    if (currentChannel) {
      sendTypingStart(currentChannel.id);

      if (typingTimeoutRef.current) clearTimeout(typingTimeoutRef.current);
      typingTimeoutRef.current = setTimeout(() => {
        if (currentChannel) sendTypingStop(currentChannel.id);
      }, 3000);
    }
  }, [currentChannel, sendTypingStart, sendTypingStop]);

  const addFiles = useCallback(async (files: File[]) => {
    if (!currentChannel) return;

    for (const file of files) {
      const newEntry: PendingFile = { file, progress: 0, status: 'uploading' };

      setPendingFiles(prev => [...prev, newEntry]);

      try {
        const urlData = await chatAttachmentsApi.getUploadUrl(
          currentChannel.id, file.name, file.type, file.size
        );

        await chatAttachmentsApi.upload(urlData.upload_url, file, (pct) => {
          setPendingFiles(prev =>
            prev.map(f =>
              f.file === file && f.status === 'uploading'
                ? { ...f, progress: pct }
                : f
            )
          );
        });

        setPendingFiles(prev =>
          prev.map(f =>
            f.file === file && f.status === 'uploading'
              ? { ...f, status: 'uploaded', fileId: urlData.file_id, progress: 100 }
              : f
          )
        );
      } catch (err) {
        console.error('Upload failed:', err);
        setPendingFiles(prev =>
          prev.map(f =>
            f.file === file && f.status === 'uploading'
              ? { ...f, status: 'error' }
              : f
          )
        );
      }
    }
  }, [currentChannel, pendingFiles.length]);

  const removePendingFile = useCallback((file: File) => {
    setPendingFiles(prev => prev.filter(f => f.file !== file));
  }, []);

  const handleSubmit = useCallback(async () => {
    if (!canSend || !currentChannel) return;

    setSending(true);
    try {
      const fileIds = uploadedFiles
        .map(f => f.fileId)
        .filter((id): id is string => !!id);

      const sent = await chatMessagesApi.send(currentChannel.id, {
        content: content.trim(),
        thread_parent_id: threadParentId,
        attachment_file_ids: fileIds.length > 0 ? fileIds : undefined,
      });
      addMessage(sent);
      setContent('');
      setPendingFiles([]);
      if (currentChannel) sendTypingStop(currentChannel.id);
      onMessageSent?.();
    } catch (e) {
      console.error('Failed to send message:', e);
    } finally {
      setSending(false);
    }
  }, [canSend, content, currentChannel, threadParentId, uploadedFiles, sendTypingStop, onMessageSent, addMessage]);

  const handleKeyDown = useCallback((e: React.KeyboardEvent<HTMLTextAreaElement>) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleSubmit();
    }
  }, [handleSubmit]);

  const handleFileDrop = useCallback(async (e: React.DragEvent) => {
    e.preventDefault();
    const files = Array.from(e.dataTransfer.files);
    if (files.length > 0) addFiles(files);
  }, [addFiles]);

  const handleFileSelect = useCallback((e: React.ChangeEvent<HTMLInputElement>) => {
    const files = Array.from(e.target.files || []);
    if (files.length > 0) addFiles(files);
    // Reset input so re-selecting same file works
    if (fileInputRef.current) fileInputRef.current.value = '';
  }, [addFiles]);

  // Auto-resize textarea
  useEffect(() => {
    const el = textareaRef.current;
    if (!el) return;
    el.style.height = 'auto';
    const maxHeight = 200;
    if (el.scrollHeight > maxHeight) {
      el.style.height = maxHeight + 'px';
      el.style.overflowY = 'auto';
    } else {
      el.style.height = el.scrollHeight + 'px';
      el.style.overflowY = 'hidden';
    }
  }, [content]);

  return (
    <div
      className="border-t bg-white px-4 py-3"
      onDragOver={(e) => e.preventDefault()}
      onDrop={handleFileDrop}
    >
      {/* Pending files strip */}
      {pendingFiles.length > 0 && (
        <div className="flex flex-wrap gap-2 mb-2">
          {pendingFiles.map((pf, idx) => (
            <div
              key={idx}
              className={`flex items-center gap-1.5 px-2 py-1 rounded text-xs border ${
                pf.status === 'error'
                  ? 'bg-red-50 border-red-200 text-red-700'
                  : pf.status === 'uploaded'
                  ? 'bg-green-50 border-green-200 text-green-700'
                  : 'bg-blue-50 border-blue-200 text-blue-700'
              }`}
            >
              {pf.status === 'uploading' && (
                <span className="text-blue-500">{pf.progress}%</span>
              )}
              {pf.status === 'uploaded' && (
                <svg className="w-3.5 h-3.5 text-green-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                </svg>
              )}
              {pf.status === 'error' && (
                <svg className="w-3.5 h-3.5 text-red-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                </svg>
              )}
              <span className="truncate max-w-[120px]">{pf.file.name}</span>
              <span className="text-gray-400">{formatFileSize(pf.file.size)}</span>
              <button
                onClick={() => removePendingFile(pf.file)}
                className="ml-0.5 text-gray-400 hover:text-gray-600"
              >
                <svg className="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
            </div>
          ))}
        </div>
      )}

      <div className="flex items-end gap-2">
        {/* Paperclip button */}
        <button
          onClick={() => fileInputRef.current?.click()}
          className="p-2 text-gray-400 hover:text-gray-600 rounded flex-shrink-0"
          title={t('attachFile')}
        >
          <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2}
              d="M15.172 7l-6.586 6.586a2 2 0 102.828 2.828l6.414-6.586a4 4 0 00-5.656-5.656l-6.415 6.585a6 6 0 108.486 8.486L20.5 13" />
          </svg>
        </button>
        <input
          ref={fileInputRef}
          type="file"
          multiple
          className="hidden"
          onChange={handleFileSelect}
        />

        <textarea
          ref={textareaRef}
          value={content}
          onChange={handleChange}
          onKeyDown={handleKeyDown}
          placeholder={t('typeMessage')}
          rows={1}
          className="flex-1 resize-none overflow-hidden border border-gray-300 bg-white text-gray-900 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
        />
        <button
          onClick={handleSubmit}
          disabled={!canSend}
          className="px-4 py-2 bg-blue-600 text-white rounded-lg text-sm font-medium hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed flex-shrink-0"
        >
          {t('send')}
        </button>
      </div>
      <div className="text-xs text-gray-400 mt-1">
        {t('enterToSend')}
      </div>
    </div>
  );
};

export default MessageInput;
